import { Component, EventEmitter, Input, Output, SimpleChanges, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../components/auth.service';
import { User, SignupUserData } from './UserInterface';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingButtonDirective } from '../../shared/directives/loading-button.directive';
import { ToggleSwitchComponent } from '../../shared/components/toggle-switch/toggle-switch.component';

export enum AuthMode {
  LOGIN = 'login',
  SIGNUP = 'signup'
}

@Component({
  selector: 'app-auth-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LoadingButtonDirective, ToggleSwitchComponent],
  templateUrl: './auth-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./auth-form.component.css']
})
export class AuthFormComponent {
  // Inputs - Configuration options
  @Input() isModal = false;
  @Input() redirectPath?: string;
  @Input() showHeader = true;
  @Input() rememberMe = true;
  @Input() initialMode: AuthMode = AuthMode.LOGIN;
  
  // Outputs - Events that container components can listen to
  @Output() authSuccess = new EventEmitter<User>();
  @Output() authError = new EventEmitter<string>();
  @Output() formCancel = new EventEmitter<void>();
  
  // Public API - Expose these to templates
  readonly AuthMode = AuthMode;
  currentMode = signal<AuthMode>(AuthMode.LOGIN);
  isSubmitting = signal<boolean>(false);
  errorMessage = signal<string>('');
  
  // Individual loading states for each auth method
  isEmailSubmitting = signal<boolean>(false);
  isGoogleSubmitting = signal<boolean>(false);
  isAppleSubmitting = signal<boolean>(false);
  
  // Private services - Inject through modern inject() function
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  
  // Forms - Login and signup forms
  loginForm: FormGroup;
  signupForm: FormGroup;

  ngOnChanges(changes: SimpleChanges): void {
    // If category changed and we have a default duration, use it
    if (changes['initialMode']) {
        this.currentMode.set(this.initialMode);
    }
  }

  constructor(
    private router: Router,
        private route: ActivatedRoute

  ) {
    
    // Initialize login form
    this.loginForm = this.fb.nonNullable.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      rememberMe: [true]
    });
    
    // Initialize signup form
    this.signupForm = this.fb.nonNullable.group({
      firstName: ['', [Validators.required, Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/)
      ]],
      confirmPassword: ['', Validators.required]
    });
    
    // Add custom validator for password confirmation
    this.signupForm.addValidators(
      () => {
        const password = this.signupForm.get('password')?.value;
        const confirmPassword = this.signupForm.get('confirmPassword')?.value;
        
        if (password !== confirmPassword) {
          this.signupForm.get('confirmPassword')?.setErrors({ mismatch: true });
          return { passwordMismatch: true };
        }
        return null;
      }
    );
  }
  
  // Public methods - UI interaction handlers
  switchMode(mode: AuthMode): void {
    // remove query param mode from the URL
    this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { mode: mode },
        queryParamsHandling: 'merge' // merge with existing params
    });
    this.currentMode.set(mode);
    this.errorMessage.set('');
  }
  
  onLoginSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }
    
    this.isSubmitting.set(true);
    this.isEmailSubmitting.set(true);
    this.errorMessage.set('');
    
    this.authService.loginUser(this.loginForm.value)
      .subscribe({
        next: (user) => {
          this.isSubmitting.set(false);
          this.isEmailSubmitting.set(false);
          this.authSuccess.emit(user);
        },
        error: (error) => {
          this.errorMessage.set(error.message || 'Login fehlgeschlagen. Bitte versuchen Sie es erneut.');
          this.isSubmitting.set(false);
          this.isEmailSubmitting.set(false);
          this.authError.emit(this.errorMessage());
        }
      });
  }
  
  onSignupSubmit(): void {
    if (this.signupForm.invalid) {
      return;
    }
    
    this.isSubmitting.set(true);
    this.isEmailSubmitting.set(true);
    this.errorMessage.set('');
    
    const userData: SignupUserData = {
      firstName: this.signupForm.value.firstName,
      lastName: this.signupForm.value.lastName,
      email: this.signupForm.value.email,
      password: this.signupForm.value.password,
      confirmPassword: this.signupForm.value.confirmPassword
    };
    
    this.authService.signupUser(userData)
      .subscribe({
        next: (user) => {
          this.isSubmitting.set(false);
          this.isEmailSubmitting.set(false);
          this.authSuccess.emit(user);
        },
        error: (error) => {
          this.errorMessage.set(error.message || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
          this.isSubmitting.set(false);
          this.isEmailSubmitting.set(false);
          this.authError.emit(this.errorMessage());
        }
      });
  }
  
  onCancel(): void {
    this.formCancel.emit();
  }

  loginWithGoogle() {
    // Prevent action if already submitting
    if (this.isSubmitting()) {
      return;
    }

    // Get remember me state from the login form
    const rememberMe = this.currentMode() === AuthMode.LOGIN ?
      this.loginForm.get('rememberMe')?.value || false :
      false;

    this.isSubmitting.set(true);
    this.isGoogleSubmitting.set(true);
    // OAuth is a full-page redirect, so returnUrl can't travel via router
    // state — AuthService persists it to sessionStorage before navigating away.
    this.authService.loginWithGoogle(rememberMe, this.route.snapshot.queryParams['returnUrl']);
  }

  loginWithApple() {
    // Prevent action if already submitting
    if (this.isSubmitting()) {
      return;
    }

    // Get remember me state from the login form
    const rememberMe = this.currentMode() === AuthMode.LOGIN ?
      this.loginForm.get('rememberMe')?.value || false :
      false;

    this.isSubmitting.set(true);
    this.isAppleSubmitting.set(true);
    this.authService.loginWithApple(rememberMe, this.route.snapshot.queryParams['returnUrl']);
  }

  // Form getters for template validation
  get loginEmail() { return this.loginForm.get('email'); }
  get loginPassword() { return this.loginForm.get('password'); }
  
  get signupFirstName() { return this.signupForm.get('firstName'); }
  get signupLastName() { return this.signupForm.get('lastName'); }
  get signupEmail() { return this.signupForm.get('email'); }
  get signupPassword() { return this.signupForm.get('password'); }
  get signupConfirmPassword() { return this.signupForm.get('confirmPassword'); }
}